import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import exportingInit from 'highcharts/modules/exporting';
import exportDataInit from 'highcharts/modules/export-data';

// Initialize the exporting and export data modules
exportingInit(Highcharts);
exportDataInit(Highcharts);

const LastDayDisaggregationChart = ({ data }) => {
    if (!data || !data.timestamps || !data.disaggregated_consumption || !data.real_consumption) {
        return null;
    }

    const { timestamps, disaggregated_consumption, real_consumption } = data;

    // Transform timestamps into proper format for x-axis
    const formattedCategories = timestamps.map(timestamp => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    });

    // Define colors for different appliances
    const applianceColors = {
        'TV': '#E91E63',              // Pink - more distinct from blue
        'Dish_Washer': '#00BCD4',     // Cyan - distinct from other colors
        'Washing_Machine': '#8BC34A', // Light Green - more distinct from orange
        'Washer_Dryer': '#9C27B0',    // Purple - keep as is, already distinct
        'Total Remaining Consumption': '#FF5722', // Deep Orange - more distinct from red
        'Real Consumption': '#424242' // Dark Grey - keep as is
    };

    // Create series data for each appliance
    const seriesData = [];

    // Add real consumption series
    seriesData.push({
        name: 'Real Consumption',
        type: 'line',
        data: real_consumption,
        color: applianceColors['Real Consumption'],
        marker: {
            enabled: true,
            radius: 2
        },
        lineWidth: 3,
        zIndex: 10,
        dashStyle: 'Solid'
    });

    // Add appliance-specific series
    Object.keys(disaggregated_consumption).forEach(appliance => {
        const applianceName = appliance.replace(/_/g, ' '); // Replace underscores with spaces for display
        seriesData.push({
            name: applianceName,
            type: 'line',
            data: disaggregated_consumption[appliance],
            color: applianceColors[appliance] || Highcharts.getOptions().colors[Object.keys(disaggregated_consumption).indexOf(appliance)],
            marker: {
                enabled: true,
                radius: 2
            },
            lineWidth: 2,
            connectNulls: false, // Don't connect points where values are 0 or null
            visible: appliance !== 'Total Remaining Consumption' // Hide 'Total Remaining Consumption' by default for clarity
        });
    });

    const chartOptions = {
        chart: {
            type: 'line',
            zoomType: 'x',
            panning: {
                enabled: true,
                type: 'x'
            },
            panKey: 'shift',
            resetZoomButton: {
                theme: {
                    fill: 'white',
                    stroke: '#bbb',
                    r: 3,
                    states: {
                        hover: {
                            fill: '#e6e6e6'
                        }
                    }
                },
                position: {
                    align: 'right',
                    verticalAlign: 'top',
                    x: -10,
                    y: 10
                }
            }
        },
        title: {
            text: null
        },
        subtitle: {
            text: 'Last Day Real vs Predicted Consumption by Appliance. Click and drag to zoom in. Hold Shift and drag to pan.',
            style: {
                fontSize: '11px',
                color: '#666'
            }
        },
        xAxis: {
            categories: formattedCategories,
            title: {
                text: 'Time'
            },
            labels: {
                rotation: -45,
                style: {
                    fontSize: '10px'
                }
            }
        },
        yAxis: {
            gridLineWidth: 0,
            title: {
                text: 'Consumption (kWh)'
            },
            min: 0
        },
        tooltip: {
            crosshairs: true,
            shared: true,
            valueSuffix: ' kWh',
            formatter: function () {
                let tooltip = `<b>${timestamps[this.x]}</b><br/>`;
                this.points.forEach(point => {
                    if (point.y > 0 || point.series.name === 'Real Consumption') { // Always show real consumption, only show non-zero values for others
                        tooltip += `${point.series.name}: <b>${point.y.toFixed(2)} kWh</b><br/>`;
                    }
                });
                return tooltip;
            }
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            itemStyle: {
                fontSize: '12px'
            }
        },
        plotOptions: {
            line: {
                marker: {
                    enabled: true,
                    radius: 2
                },
                lineWidth: 2
            }
        },
        series: seriesData,
        exporting: {
            enabled: true
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: false
        }
    };

    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '100%', width: '100%' } }}
        />
    );
};

export default LastDayDisaggregationChart; 